<template>
  <v-dialog
    :value="value"
    persistent
    max-width="480"
    content-class="wrestler-search-dialog"
    @input="$emit('input', $event)"
  >
    <v-card class="wrestler-search-card">
      <div class="wrestler-search-header">
        <h3 class="wrestler-search-title">Search wrestler</h3>
        <v-btn icon class="wrestler-search-close" @click="close">
          <v-icon>close</v-icon>
        </v-btn>
      </div>
      <v-card-text class="wrestler-search-body">
        <div class="wrestler-search-filters">
          <v-text-field
            v-model="filters.name"
            label="Name"
            placeholder="First or last name"
            outline
            dense
            hide-details
            clearable
            class="wrestler-search-field"
            @keydown.enter="runSearch"
          />
          <v-text-field
            v-model="filters.school"
            label="School / Team"
            placeholder="School name"
            outline
            dense
            hide-details
            clearable
            class="wrestler-search-field"
            @keydown.enter="runSearch"
          />
          <v-select
            v-model="filters.state"
            :items="stateOptions"
            label="State"
            placeholder="Any state"
            outline
            dense
            hide-details
            clearable
            class="wrestler-search-field"
          />
          <v-text-field
            v-model="filters.weight"
            label="Weight class"
            placeholder="e.g. 70, 120"
            outline
            dense
            hide-details
            clearable
            class="wrestler-search-field"
            @keydown.enter="runSearch"
          />
          <v-btn color="primary" class="wrestler-search-btn" @click="runSearch">
            Search
          </v-btn>
        </div>
        <div class="wrestler-search-results-wrap">
          <div v-if="searching" class="wrestler-search-loading">
            <v-progress-circular indeterminate color="primary" size="32" />
            <span>Searching…</span>
          </div>
          <div
            v-else-if="searchResults.length === 0 && hasSearched"
            class="wrestler-search-empty"
          >
            No wrestlers match. Try loosening the filters.
          </div>
          <div
            v-else-if="searchResults.length === 0 && !hasSearched"
            class="wrestler-search-empty"
          >
            Enter filters and click Search.
          </div>
          <div v-else ref="resultsList" class="wrestler-search-results">
            <div
              v-for="item in searchResults"
              :key="item.value"
              class="wrestler-search-row"
              @click="selectWrestler(item)"
            >
              <div class="wrestler-search-row-avatar">
                <img
                  :src="
                    authenticated && item.image ? item.image : defaultAvatar
                  "
                  alt=""
                  class="wrestler-search-avatar-img"
                />
              </div>
              <div class="wrestler-search-row-content">
                <div class="wrestler-search-row-name">{{ item.text }}</div>
                <div class="wrestler-search-row-meta">
                  <span v-if="item.school">{{ item.school }}</span>
                  <span v-if="item.school && item.weight"> · </span>
                  <span v-if="item.weight">{{ item.weight }}</span>
                  <span v-if="(item.school || item.weight) && item.leagueName">
                    · {{ item.leagueName }}
                  </span>
                </div>
              </div>
            </div>
            <div v-if="hasMore && !searching" class="wrestler-search-load-more">
              <v-btn flat small color="primary" @click="loadMore"
                >Load more</v-btn
              >
            </div>
          </div>

          <div class="wrestler-search-quick-add">
            <v-btn
              flat
              small
              color="primary"
              class="quick-add-toggle"
              @click="showQuickAdd = !showQuickAdd"
            >
              <v-icon left small>{{
                showQuickAdd ? "expand_less" : "person_add"
              }}</v-icon>
              {{ showQuickAdd ? "Hide" : "Add new wrestler" }}
            </v-btn>
            <v-expand-transition>
              <div v-if="showQuickAdd" class="quick-add-form">
                <v-text-field
                  v-model="quickAdd.firstName"
                  label="First name"
                  outline
                  dense
                  hide-details
                  class="quick-add-field"
                />
                <v-text-field
                  v-model="quickAdd.lastName"
                  label="Last name"
                  outline
                  dense
                  hide-details
                  class="quick-add-field"
                />
                <v-text-field
                  v-model="quickAdd.school"
                  label="School / Team"
                  outline
                  dense
                  hide-details
                  class="quick-add-field"
                />
                <v-select
                  v-model="quickAdd.league"
                  :items="leaguesList"
                  item-text="text"
                  item-value="value"
                  label="League"
                  outline
                  dense
                  hide-details
                  clearable
                  return-object
                  class="quick-add-field"
                  @change="onQuickAddLeagueChange"
                />
                <v-select
                  v-model="quickAdd.weightClass"
                  :items="weightClassOptions"
                  label="Weight class"
                  outline
                  dense
                  hide-details
                  clearable
                  class="quick-add-field"
                />
                <v-text-field
                  v-model="quickAdd.grade"
                  label="Grade (optional)"
                  outline
                  dense
                  hide-details
                  class="quick-add-field"
                />
                <v-btn
                  color="primary"
                  :loading="addingWrestler"
                  :disabled="!quickAdd.firstName || !quickAdd.lastName"
                  class="quick-add-submit"
                  @click="submitQuickAdd"
                >
                  Add wrestler
                </v-btn>
              </div>
            </v-expand-transition>
          </div>

          <v-dialog
            v-model="showDuplicateDialog"
            max-width="440"
            persistent
            content-class="wrestler-search-dup-dialog"
          >
            <v-card>
              <v-card-title class="subheading"
                >Possible duplicate wrestler(s)</v-card-title
              >
              <v-card-text>
                <p class="dup-dialog-p">
                  Someone with a similar name exists in this school and weight
                  class:
                </p>
                <v-list dense>
                  <v-list-item
                    v-for="dup in potentialDuplicates"
                    :key="dup.id"
                    class="dup-list-item"
                    @click="useExistingWrestler(dup)"
                  >
                    <v-list-item-content>
                      <v-list-item-title>{{
                        dup.FullName || dup.FirstName + " " + dup.LastName
                      }}</v-list-item-title>
                      <v-list-item-subtitle
                        >{{ dup.School }} ·
                        {{ dup.RankWeightClass }}</v-list-item-subtitle
                      >
                    </v-list-item-content>
                    <v-icon small>arrow_forward</v-icon>
                  </v-list-item>
                </v-list>
                <p class="dup-dialog-p mt-2">
                  Use one above or add a new wrestler anyway.
                </p>
              </v-card-text>
              <v-card-actions>
                <v-btn flat @click="cancelDuplicate">Cancel</v-btn>
                <v-spacer />
                <v-btn
                  color="primary"
                  :loading="addingWrestler"
                  @click="proceedWithAddWrestler"
                >
                  Add new anyway
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  getFirestore,
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  startAfter,
  addDoc,
} from "firebase/firestore";
import { searchForDuplicateWrestlers } from "../utils/wrestlerDuplicateCheck";
import { authState } from "../utils/authState";

const defaultAvatar = require("../assets/default-profile.png");

const PAGE_SIZE = 200;

export default {
  name: "WrestlerSearchDialog",
  props: {
    value: { type: Boolean, default: false },
    excludeId: { type: String, default: "" },
  },
  data() {
    return {
      filters: {
        name: "",
        school: "",
        state: "",
        weight: "",
      },
      stateOptions: [],
      leagueNameToState: {},
      leaguesList: [],
      searchResults: [],
      allAthletes: [],
      lastDoc: null,
      searching: false,
      hasSearched: false,
      showQuickAdd: false,
      quickAdd: {
        firstName: "",
        lastName: "",
        school: "",
        league: null,
        weightClass: "",
        grade: "",
      },
      weightClassOptions: [],
      showDuplicateDialog: false,
      defaultAvatar,
      potentialDuplicates: [],
      pendingQuickAdd: null,
      addingWrestler: false,
    };
  },
  computed: {
    authenticated() {
      return authState.user != null;
    },
    hasMore() {
      return this.lastDoc !== null;
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.searchResults = [];
        this.allAthletes = [];
        this.lastDoc = null;
        this.hasSearched = false;
        this.filters = { name: "", school: "", state: "", weight: "" };
        this.showQuickAdd = false;
        this.quickAdd = {
          firstName: "",
          lastName: "",
          school: "",
          league: null,
          weightClass: "",
          grade: "",
        };
        this.weightClassOptions = [];
        this.showDuplicateDialog = false;
        this.potentialDuplicates = [];
        this.pendingQuickAdd = null;
        this.loadLeagues();
      }
    },
  },
  methods: {
    close() {
      this.$emit("input", false);
    },
    async loadLeagues() {
      const db = getFirestore();
      try {
        const q = query(collection(db, "leagues"), orderBy("Name"));
        const snap = await getDocs(q);
        const states = new Set();
        const map = {};
        const list = [];
        snap.forEach((d) => {
          const data = d.data() || {};
          const name =
            data.Name != null && String(data.Name).trim() !== ""
              ? String(data.Name).trim()
              : null;
          const state =
            data.State != null && String(data.State).trim() !== ""
              ? String(data.State).trim()
              : "";
          if (name) {
            map[name] = state;
            if (state) states.add(state);
            list.push({ text: name, value: d.id });
          }
        });
        this.leagueNameToState = map;
        this.stateOptions = Array.from(states).sort();
        this.leaguesList = list;
      } catch (_) {
        this.stateOptions = [];
        this.leagueNameToState = {};
        this.leaguesList = [];
      }
    },
    onQuickAddLeagueChange() {
      const leagueId = this.quickAdd.league && this.quickAdd.league.value;
      if (leagueId) this.loadWeightclasses(leagueId);
      else this.weightClassOptions = [];
      this.quickAdd.weightClass = "";
    },
    async loadWeightclasses(leagueId) {
      const db = getFirestore();
      try {
        const q = query(
          collection(db, "leagues", leagueId, "weightclasses"),
          orderBy("Weight")
        );
        const snap = await getDocs(q);
        this.weightClassOptions = snap.docs
          .map((d) => d.data().Weight)
          .filter((w) => w != null);
      } catch (_) {
        this.weightClassOptions = [];
      }
    },
    async submitQuickAdd() {
      const first = (this.quickAdd.firstName || "").trim();
      const last = (this.quickAdd.lastName || "").trim();
      if (!first || !last) return;
      this.addingWrestler = true;
      try {
        const dups = await searchForDuplicateWrestlers({
          firstName: first,
          lastName: last,
          school: (this.quickAdd.school || "").trim(),
          rankWeightClass: (this.quickAdd.weightClass || "").trim(),
        });
        if (dups && dups.length > 0) {
          this.potentialDuplicates = dups;
          this.pendingQuickAdd = {
            firstName: first,
            lastName: last,
            school: (this.quickAdd.school || "").trim(),
            league: this.quickAdd.league,
            weightClass: (this.quickAdd.weightClass || "").trim(),
            grade: (this.quickAdd.grade || "").trim(),
          };
          this.showDuplicateDialog = true;
          return;
        }
        await this.doAddWrestler({
          firstName: first,
          lastName: last,
          school: (this.quickAdd.school || "").trim(),
          league: this.quickAdd.league,
          weightClass: (this.quickAdd.weightClass || "").trim(),
          grade: (this.quickAdd.grade || "").trim(),
        });
      } finally {
        this.addingWrestler = false;
      }
    },
    async doAddWrestler(payload) {
      const db = getFirestore();
      const leagueName =
        (payload.league && payload.league.text) || payload.league || "";
      const fullName =
        (payload.firstName || "") + " " + (payload.lastName || "");
      const docRef = await addDoc(collection(db, "athletes"), {
        FirstName: payload.firstName,
        LastName: payload.lastName,
        FullName: fullName.trim(),
        School: payload.school || "",
        Grade: payload.grade || "",
        RankLeague: leagueName,
        RankWeightClass: payload.weightClass || "",
        Image: "",
        Skill: 1200,
        Wins: 0,
        Losses: 0,
      });
      this.showDuplicateDialog = false;
      this.pendingQuickAdd = null;
      this.showQuickAdd = false;
      this.quickAdd = {
        firstName: "",
        lastName: "",
        school: "",
        league: null,
        weightClass: "",
        grade: "",
      };
      this.weightClassOptions = [];
      this.$emit("select", {
        text: fullName.trim(),
        value: docRef.id,
        skill: 1200,
        wins: 0,
        losses: 0,
        image: "",
      });
      this.close();
    },
    proceedWithAddWrestler() {
      if (!this.pendingQuickAdd) return;
      this.addingWrestler = true;
      this.doAddWrestler(this.pendingQuickAdd).finally(() => {
        this.addingWrestler = false;
      });
    },
    useExistingWrestler(dup) {
      const fullName =
        dup.FullName ||
        [dup.FirstName, dup.LastName].filter(Boolean).join(" ") ||
        "—";
      this.$emit("select", {
        text: fullName,
        value: dup.id,
        skill: dup.Skill,
        wins: dup.Wins,
        losses: dup.Losses,
        image: dup.Image || "",
      });
      this.showDuplicateDialog = false;
      this.pendingQuickAdd = null;
      this.close();
    },
    cancelDuplicate() {
      this.showDuplicateDialog = false;
      this.pendingQuickAdd = null;
    },
    async runSearch() {
      this.hasSearched = true;
      this.searchResults = [];
      this.allAthletes = [];
      this.lastDoc = null;
      this.searching = true;
      await this.loadNextBatch();
      this.searching = false;
    },
    async loadNextBatch() {
      const db = getFirestore();
      const constraints = [orderBy("FullName"), limit(PAGE_SIZE)];
      let q = query(collection(db, "athletes"), ...constraints);
      if (this.lastDoc) q = query(q, startAfter(this.lastDoc));
      const snap = await getDocs(q);
      const batch = [];
      snap.forEach((d) => {
        if (d.id === this.excludeId) return;
        const data = d.data() || {};
        const fullName =
          data.FullName || (data.FirstName + " " + data.LastName).trim() || "—";
        batch.push({
          id: d.id,
          text: fullName,
          value: d.id,
          skill: data.Skill,
          wins: data.Wins,
          losses: data.Losses,
          image: data.Image || "",
          school:
            data.School != null && String(data.School).trim() !== ""
              ? String(data.School).trim()
              : "",
          weight:
            data.RankWeightClass != null &&
            String(data.RankWeightClass).trim() !== ""
              ? String(data.RankWeightClass).trim()
              : "",
          leagueName:
            data.RankLeague != null && String(data.RankLeague).trim() !== ""
              ? String(data.RankLeague).trim()
              : "",
          firstName:
            data.FirstName != null && String(data.FirstName).trim() !== ""
              ? String(data.FirstName).trim()
              : "",
          lastName:
            data.LastName != null && String(data.LastName).trim() !== ""
              ? String(data.LastName).trim()
              : "",
        });
      });
      this.allAthletes.push(...batch);
      if (snap.docs.length < PAGE_SIZE) this.lastDoc = null;
      else this.lastDoc = snap.docs[snap.docs.length - 1];
      this.applyFilters();
    },
    applyFilters() {
      const name = (this.filters.name || "").trim().toLowerCase();
      const school = (this.filters.school || "").trim().toLowerCase();
      const state = (this.filters.state || "").trim();
      const weight = (this.filters.weight || "").trim().toLowerCase();
      let list = this.allAthletes;
      if (name) {
        list = list.filter(
          (a) =>
            (a.text && a.text.toLowerCase().includes(name)) ||
            (a.firstName && a.firstName.toLowerCase().includes(name)) ||
            (a.lastName && a.lastName.toLowerCase().includes(name))
        );
      }
      if (school) {
        list = list.filter(
          (a) => a.school && a.school.toLowerCase().includes(school)
        );
      }
      if (state) {
        list = list.filter((a) => {
          const leagueState = this.leagueNameToState[a.leagueName];
          return leagueState === state;
        });
      }
      if (weight) {
        list = list.filter(
          (a) => a.weight && a.weight.toString().toLowerCase().includes(weight)
        );
      }
      this.searchResults = list;
    },
    async loadMore() {
      if (this.searching || !this.lastDoc) return;
      this.searching = true;
      await this.loadNextBatch();
      this.searching = false;
    },
    selectWrestler(item) {
      this.$emit("select", {
        text: item.text,
        value: item.value,
        skill: item.skill,
        wins: item.wins,
        losses: item.losses,
        image: item.image,
      });
      this.close();
    },
  },
};
</script>

<style scoped>
.wrestler-search-card {
  border-radius: 10px;
  overflow: hidden;
}
.wrestler-search-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #c62828 0%, #b71c1c 100%);
  color: #fff;
}
.wrestler-search-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}
.wrestler-search-close {
  color: #fff;
}
.wrestler-search-body {
  padding: 1rem;
  max-height: 70vh;
  overflow-y: auto;
}
.wrestler-search-filters {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}
.wrestler-search-field {
  font-size: 0.9rem;
}
.wrestler-search-btn {
  margin-top: 0.25rem;
}
.wrestler-search-results-wrap {
  min-height: 120px;
}
.wrestler-search-loading,
.wrestler-search-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1.5rem;
  color: #6b7280;
  font-size: 0.9rem;
}
.wrestler-search-results {
  max-height: 320px;
  overflow-y: auto;
}
.wrestler-search-row {
  display: flex;
  align-items: center;
  padding: 0.6rem 0.75rem;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  transition: background 0.15s;
}
.wrestler-search-row:hover {
  background: #f3f4f6;
}
.wrestler-search-row-avatar {
  margin-right: 0.75rem;
  flex-shrink: 0;
}
.wrestler-search-avatar-img {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
}
.wrestler-search-row-content {
  min-width: 0;
}
.wrestler-search-row-name {
  font-weight: 600;
  font-size: 0.95rem;
  color: #1f2937;
}
.wrestler-search-row-meta {
  font-size: 0.8rem;
  color: #6b7280;
  margin-top: 0.15rem;
}
.wrestler-search-load-more {
  padding: 0.5rem;
  text-align: center;
}
.theme--dark .wrestler-search-row {
  border-bottom-color: #374151;
}
.theme--dark .wrestler-search-row:hover {
  background: #1f2937;
}
.theme--dark .wrestler-search-row-name {
  color: #e5e7eb;
}
.theme--dark .wrestler-search-row-meta {
  color: #9ca3af;
}
.wrestler-search-quick-add {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #eee;
}
.theme--dark .wrestler-search-quick-add {
  border-top-color: #374151;
}
.quick-add-toggle {
  margin-bottom: 0.5rem;
}
.quick-add-form {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.5rem 0;
}
.quick-add-field {
  font-size: 0.9rem;
}
.quick-add-submit {
  margin-top: 0.25rem;
}
.dup-dialog-p {
  margin: 0 0 0.5rem;
  font-size: 0.9rem;
  color: #374151;
}
.theme--dark .dup-dialog-p {
  color: #e5e7eb;
}
.dup-list-item {
  cursor: pointer;
}
.dup-list-item:hover {
  background: #f3f4f6;
}
.theme--dark .dup-list-item:hover {
  background: #1f2937;
}
</style>
